import {Component, DestroyRef, ElementRef, OnInit, ViewChild, inject, signal} from '@angular/core';
import {takeUntilDestroyed, toObservable} from '@angular/core/rxjs-interop';
import {CommonModule} from '@angular/common';
import {Router} from '@angular/router';
import {Observable, Subscription, skip} from 'rxjs';
import {
  PureOriginalActivityPage,
  PureOriginalActivityRow,
  PureOriginalExportTarget,
  PureOriginalExportStats,
  TopTuningPerEcuBuildStats,
  TrendGranularity
} from '../../models/dashboard-statistics.model';
import {DashboardStatisticsService} from '../../services/dashboard-statistics.service';
import {DashboardPeriodService, PeriodMode} from '../../../dashboard/services/dashboard-period.service';

@Component({
  selector: 'app-po-analytics',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './po-analytics.component.html',
  styleUrls: ['./po-analytics.component.css']
})
export class PoAnalyticsComponent implements OnInit {

  private readonly statsService = inject(DashboardStatisticsService);
  private readonly periodService = inject(DashboardPeriodService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);

  @ViewChild('activityDialog') private activityDialog!: ElementRef<HTMLDialogElement>;
  private activityRequest?: Subscription;

  private readonly mode$: Observable<PeriodMode> = toObservable(this.periodService.mode);

  // The Export Stats card has its own Day/Week/Month filter that drives its own
  // date range, independent of the global date selector at the top of the page.
  exportStatsGranularity = signal<TrendGranularity>('DAY');

  exportStats: PureOriginalExportStats | null = null;
  popularStage1: TopTuningPerEcuBuildStats[] = [];
  isLoading = signal<boolean>(true);
  readonly activityTarget = signal<PureOriginalExportTarget | null>(null);
  readonly activityTitle = signal('');
  readonly activityRangeLabel = signal('');
  readonly activityPage = signal<PureOriginalActivityPage | null>(null);
  readonly activityPageNumber = signal(0);
  readonly activityLoading = signal(false);
  readonly activityError = signal(false);

  ngOnInit(): void {
    this.isLoading.set(true);
    let pending = 2;
    const done = () => { if (--pending === 0) this.isLoading.set(false); };
    this.loadExportStats(done);
    this.loadPopularStage1(done);

    // Only the Popular Tunings card follows the global selector; the Export Stats
    // card keeps its own local range, so global changes don't touch it.
    this.mode$.pipe(skip(1), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.isLoading.set(true);
      this.loadPopularStage1(() => this.isLoading.set(false));
    });
  }

  onExportStatsGranularityChange(g: TrendGranularity): void {
    this.exportStatsGranularity.set(g);
    this.isLoading.set(true);
    this.loadExportStats(() => this.isLoading.set(false));
  }

  openActivity(target: PureOriginalExportTarget, title: string, count: number): void {
    if (!count) return;
    const {start, end} = this.exportStatsRange();
    this.activityTarget.set(target);
    this.activityTitle.set(title);
    this.activityRangeLabel.set(`${start.replace('T', ' ')} – ${end.replace('T', ' ')}`);
    this.activityPageNumber.set(0);
    this.activityDialog.nativeElement.showModal();
    this.loadActivityPage(0);
  }

  closeActivity(): void {
    this.activityDialog.nativeElement.close();
  }

  onActivityClosed(): void {
    this.activityRequest?.unsubscribe();
    this.activityTarget.set(null);
    this.activityPage.set(null);
    this.activityLoading.set(false);
  }

  goToActivityPage(pageNumber: number): void {
    const page = this.activityPage();
    if (pageNumber < 0 || (page && pageNumber >= page.totalPages) || this.activityLoading()) return;
    this.loadActivityPage(pageNumber);
  }

  retryActivity(): void {
    this.loadActivityPage(this.activityPageNumber());
  }

  openActivityDetails(row: PureOriginalActivityRow): void {
    this.closeActivity();
    this.router.navigate(['/app/pure-originals/pure-original-details'], {
      queryParams: {pureOriginalId: row.pureOriginalId}
    });
  }

  formatActivityDate(value: string | null): string {
    if (!value) return '—';
    const match = /^(\d{2})-(\d{2})-(\d{4})-(\d{2})-(\d{2})-(\d{2})$/.exec(value);
    return match ? `${match[1]}.${match[2]}.${match[3]} ${match[4]}:${match[5]}:${match[6]}` : value.replace('T', ' ');
  }

  private loadActivityPage(pageNumber: number): void {
    const target = this.activityTarget();
    if (!target) return;
    this.activityRequest?.unsubscribe();
    this.activityPageNumber.set(pageNumber);
    this.activityPage.set(null);
    this.activityLoading.set(true);
    this.activityError.set(false);
    const {start, end} = this.exportStatsRange();
    // Export stats include the final second; the activity endpoint uses an exclusive end.
    const exclusiveEnd = this.toExclusiveEnd(end);
    this.activityRequest = this.statsService.getPureOriginalActivity(start, exclusiveEnd, target, pageNumber, 20)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: page => {
          this.activityPage.set(page);
          this.activityLoading.set(false);
        },
        error: () => {
          this.activityError.set(true);
          this.activityLoading.set(false);
        }
      });
  }

  private toExclusiveEnd(end: string): string {
    const date = new Date(end);
    date.setSeconds(date.getSeconds() + 1);
    const pad = (value: number) => String(value).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
      `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  }

  /** Local date range derived from the Export Stats card's own Day/Week/Month switch. */
  private exportStatsRange(): {start: string; end: string} {
    const g = this.exportStatsGranularity();
    const mode: Exclude<PeriodMode, 'CUSTOM'> = g === 'DAY' ? 'TODAY' : g;
    return this.periodService.presetRange(mode);
  }

  private loadExportStats(done?: () => void): void {
    const {start, end} = this.exportStatsRange();
    this.statsService.getPureOriginalExportStats(start, end)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: stats => {
          this.exportStats = stats;
          done?.();
        },
        error: () => done?.()
      });
  }

  private loadPopularStage1(done?: () => void): void {
    const start = this.periodService.currentStart();
    const end = this.periodService.currentEnd();
    this.statsService.getPopularStage1ByEcuBuild(start, end)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: stage1 => {
          this.popularStage1 = stage1;
          done?.();
        },
        error: () => done?.()
      });
  }

  get topStage1(): TopTuningPerEcuBuildStats[] {
    return this.popularStage1.slice(0, 5);
  }
}
